import { useMemo, useState } from "react";
import type { DoseEventRow } from "@/lib/database.types";

// ---------------------------------------------------------------------------
// Estimated medication levels — pharmacokinetic decay model.
//
// Each logged dose contributes  dose_mg * 0.5^(hoursSince / halfLifeHours)
// and the plotted level is the sum of all contributions (superposition).
// This is an educational estimate, not a serum measurement — long-half-life
// GLP-1s accumulate toward steady state, and the curve makes titration and
// missed doses legible to the patient.
// ---------------------------------------------------------------------------

// Published elimination half-lives (hours), keyed by substring match.
const HALF_LIFE_HOURS: Array<[string, number]> = [
  ["semaglutide", 168],   // ~7 days
  ["tirzepatide", 120],   // ~5 days
  ["testosterone", 192],  // cypionate ~8 days
  ["enclomiphene", 10],
];
const DEFAULT_HALF_LIFE = 120;

export function halfLifeFor(medication: string): number {
  const med = medication.toLowerCase();
  for (const [key, hours] of HALF_LIFE_HOURS) {
    if (med.includes(key)) return hours;
  }
  return DEFAULT_HALF_LIFE;
}

export function estimatedLevelAt(doses: DoseEventRow[], atMs: number): number {
  let level = 0;
  for (const d of doses) {
    const takenMs = new Date(d.taken_at).getTime();
    if (takenMs > atMs) continue;
    const hours = (atMs - takenMs) / 3_600_000;
    level += Number(d.dose_mg) * Math.pow(0.5, hours / halfLifeFor(d.medication));
  }
  return level;
}

type RangeKey = "week" | "month" | "90d" | "all";
const RANGES: Array<{ key: RangeKey; label: string; days: number | null }> = [
  { key: "week",  label: "Week",    days: 7 },
  { key: "month", label: "Month",   days: 30 },
  { key: "90d",   label: "90 days", days: 90 },
  { key: "all",   label: "All time", days: null },
];

const PROJECT_DAYS = 7; // dotted look-ahead past "now"
const SAMPLES = 140;

// Chart geometry (SVG user units).
const W = 660;
const H = 220;
const PAD = { top: 12, right: 10, bottom: 26, left: 40 };

function fmtTick(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
}

export function MedLevelChart({ doses }: { doses: DoseEventRow[] }) {
  const [range, setRange] = useState<RangeKey>("week");

  const model = useMemo(() => {
    if (doses.length === 0) return null;
    const nowMs = Date.now();
    const takenTimes = doses.map((d) => new Date(d.taken_at).getTime());
    const firstMs = Math.min(...takenTimes);

    const days = RANGES.find((r) => r.key === range)!.days;
    const startMs = days == null ? firstMs : nowMs - days * 86_400_000;
    const endMs = nowMs + PROJECT_DAYS * 86_400_000;

    // Projection assumes the patient continues their cadence: one more dose,
    // same mg as the last logged one, one week after it (if still ahead of us).
    const lastDose = doses.reduce((a, b) =>
      new Date(a.taken_at).getTime() >= new Date(b.taken_at).getTime() ? a : b,
    );
    const projected: DoseEventRow[] = [];
    const nextMs = new Date(lastDose.taken_at).getTime() + 7 * 86_400_000;
    if (nextMs > nowMs && nextMs < endMs) {
      projected.push({ ...lastDose, id: "projected", taken_at: new Date(nextMs).toISOString() });
    }
    const all = [...doses, ...projected];

    const pts: Array<{ ms: number; level: number }> = [];
    for (let i = 0; i <= SAMPLES; i++) {
      const ms = startMs + ((endMs - startMs) * i) / SAMPLES;
      pts.push({ ms, level: estimatedLevelAt(all, ms) });
    }
    const maxLevel = Math.max(0.1, ...pts.map((p) => p.level));
    return { pts, nowMs, startMs, endMs, maxLevel, nowLevel: estimatedLevelAt(doses, nowMs) };
  }, [doses, range]);

  if (!model) {
    return (
      <p className="text-[13px] text-fh-gray">
        Log your first dose and your estimated medication level will chart here.
      </p>
    );
  }

  const { pts, nowMs, startMs, endMs, maxLevel } = model;
  const x = (ms: number) =>
    PAD.left + ((ms - startMs) / (endMs - startMs)) * (W - PAD.left - PAD.right);
  const y = (level: number) =>
    PAD.top + (1 - level / (maxLevel * 1.15)) * (H - PAD.top - PAD.bottom);

  const past = pts.filter((p) => p.ms <= nowMs);
  const future = pts.filter((p) => p.ms >= nowMs);
  const line = (arr: typeof pts) =>
    arr.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.ms).toFixed(1)},${y(p.level).toFixed(1)}`).join(" ");
  const area =
    past.length > 1
      ? `${line(past)} L${x(nowMs).toFixed(1)},${y(0).toFixed(1)} L${x(startMs).toFixed(1)},${y(0).toFixed(1)} Z`
      : "";

  // Axis ticks: 4 evenly-spaced dates + 3 mg gridlines.
  const xTicks = [0, 1 / 3, 2 / 3, 1].map((f) => startMs + f * (endMs - startMs));
  const yTicks = [0, maxLevel / 2, maxLevel];

  return (
    <div>
      {/* Range toggle */}
      <div className="flex gap-1 mb-3 bg-fh-sand rounded-[10px] p-1 w-fit">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            aria-pressed={range === r.key}
            className={[
              "px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition",
              range === r.key ? "bg-white text-fh-ink shadow-soft" : "text-fh-gray hover:text-fh-ink",
            ].join(" ")}
          >
            {r.label}
          </button>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
        aria-label="Estimated medication level over time">
        {/* mg gridlines + labels */}
        {yTicks.map((lv, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(lv)} y2={y(lv)}
              stroke="#e7e9ec" strokeWidth="1" />
            <text x={PAD.left - 6} y={y(lv) + 3.5} textAnchor="end"
              fontSize="10.5" fill="#6b7178">
              {lv.toFixed(lv >= 10 ? 0 : 1)}mg
            </text>
          </g>
        ))}
        {/* date ticks */}
        {xTicks.map((ms, i) => (
          <text key={i} x={x(ms)} y={H - 8} textAnchor="middle" fontSize="10.5" fill="#6b7178">
            {fmtTick(ms)}
          </text>
        ))}
        {/* actual: filled area + solid line */}
        {area && <path d={area} fill="#4a7c59" opacity="0.12" />}
        {past.length > 1 && (
          <path d={line(past)} fill="none" stroke="#2d5a3d" strokeWidth="2" strokeLinejoin="round" />
        )}
        {/* projection: dotted */}
        {future.length > 1 && (
          <path d={line(future)} fill="none" stroke="#4a7c59" strokeWidth="2"
            strokeDasharray="3.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
        )}
        {/* "now" marker */}
        <line x1={x(nowMs)} x2={x(nowMs)} y1={PAD.top} y2={H - PAD.bottom}
          stroke="#C9956A" strokeWidth="1" strokeDasharray="2 3" />
        <circle cx={x(nowMs)} cy={y(estimatedLevelAt(doses, nowMs))} r="4"
          fill="#2d5a3d" stroke="#fff" strokeWidth="1.5" />
      </svg>

      <p className="text-[11.5px] text-fh-gray-light mt-2 leading-snug">
        Estimated from your logged doses using each medication's published half-life.
        Dotted segment projects your next weekly dose. Educational estimate — not a lab measurement.
      </p>
    </div>
  );
}
